import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';
import sanitizeHtml from 'sanitize-html';
import { Parser } from 'htmlparser2';
import { API_URL } from '..';
import ReportPDF from './ReportPDF';

// Register Montserrat font for PDF


// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    color: '#000',
    lineHeight: 1.5,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 50,
  },
  companyInfo: {
    width: '50%',
  },
  logo: {
    width: 90,
    marginBottom: 5,
  },
  invoiceTitle: {
    fontSize: 18,
    fontWeight: 600,
    paddingBottom: 30,
    textAlign: 'center',
    marginVertical: 10,
    borderBottom: 3,
    borderBottomColor: '#d17f15',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#d17f15',
    color: '#fff',
    padding: 8,
    fontSize: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: 1,
    borderColor: '#c0c0c0',
    padding: 5,
    borderStyle: 'dotted'
  },
  tableColDesc: {
    width: '60%',
    fontSize: 10,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColQty: {
    width: '20%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 10,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColUnitPrice: {
    width: '20%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 10,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColPrice: {
    width: '20%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 10,
  },
  paymentSection: {
    flexDirection: 'row',
    marginTop: 15,
  },
  paymentInstructions: {
    width: '50%',
    backgroundColor: '#f2f2f2',
    borderWidth: 2,
    borderColor: '#d17f15',
    textAlign: 'center',
    padding: 5,
  },
  totals: {
    width: '50%',
    textAlign: 'right',
  },
  totalRow: {
    padding: 5,
    fontSize: 12,
    backgroundColor: '#f3f3f3',
  },
  totalRowBold: {
    padding: 5,
    fontSize: 13,
    fontWeight: 700,
  },
  divider: {
    borderBottom: 1,
    borderColor: '#d17f15',
    marginVertical: 5,
  },
  termsSection: {
    marginTop: 15,
    fontSize: 12,
    textAlign: 'center',
  },
  footerLogos: {
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: 8,
    borderTop: 1,
    borderColor: '#c0c0c0',
    paddingTop: 15,
    marginTop: 40,
  },
  footerLogo: {
    objectFit: 'contain'
  },
  descriptionParagraph: {
    marginBottom: 0,
    fontSize: 10,
    lineHeight: 1.8,
  },
  descriptionBold: {
    fontWeight: 700,
    fontSize: 10,
    lineHeight: 1.8,
  },
  listItem: {
    marginLeft: 5,
    marginBottom: 3,
  },
});

// Helper: Convert image URL to base64
const toBase64 = async (url) => {
  try {
    const response = await fetch(`${API_URL}/proxy-image?url=${encodeURIComponent(url)}`, {
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', url, error);
    return url;
  }
};

// Helper: Parse HTML for PDF rendering
const parseHtmlToPdf = (html) => {
  const elements = [];
  let currentText = '';
  let currentStyles = {};
  let isInsideList = false;
  let isInsideListItem = false;
  let listItemIndex = 0; // Track index for ordered lists

  const parser = new Parser(
    {
      onopentag(name, attribs) {
        if (name === 'p') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={styles.descriptionParagraph}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          currentStyles = { ...styles.descriptionParagraph };
        } else if (name === 'strong') {
          currentStyles = { ...currentStyles, ...styles.descriptionBold };
        } else if (name === 'br') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          elements.push(
            <Text key={elements.length} style={currentStyles}>
              {'\n'}
            </Text>
          );
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = true;
          listItemIndex = 0; // Reset index at the start of a new list
        } else if (name === 'li') {
          isInsideListItem = true;
          listItemIndex += 1; // Increment index for ordered lists
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          currentStyles = { ...styles.descriptionParagraph, ...styles.listItem };
          // Determine prefix based on data-list attribute
          if (attribs['data-list'] === 'ordered') {
            currentText = `${listItemIndex}. `;
          } else if (attribs['data-list'] === 'bullet') {
            currentText = '• ';
          } else {
            currentText = '• '; // Default to bullet if data-list is not specified
          }
        }
      },
      ontext(text) {
        currentText += text.trim();
      },
      onclosetag(name) {
        if (name === 'p' || name === 'strong' || name === 'li') {
          if (currentText) {
            elements.push(
              <Text key={elements.length} style={currentStyles}>
                {currentText}
              </Text>
            );
            currentText = '';
          }
          if (name === 'strong') {
            currentStyles = { ...styles.descriptionParagraph };
          } else if (name === 'li') {
            isInsideListItem = false;
            currentStyles = { ...styles.descriptionParagraph };
          }
        } else if (name === 'ul' || name === 'ol') {
          isInsideList = false;
          listItemIndex = 0; // Reset index at the end of a list
        }
      },
    },
    { decodeEntities: true }
  );

  parser.write(
    sanitizeHtml(html, {
      allowedTags: ['p', 'strong', 'br', 'ul', 'ol', 'li'],
      allowedAttributes: {
        li: ['data-list'],
      },
    })
  );
  parser.end();

  if (currentText) {
    elements.push(
      <Text key={elements.length} style={styles.descriptionParagraph}>
        {currentText}
      </Text>
    );
  }

  return elements.length > 0 ? (
    <View>{elements}</View>
  ) : (
    <Text style={styles.descriptionParagraph}>N/A</Text>
  );
};

// Invoice PDF Component
const InvoicePDF = ({ data, showLogo, showAddress, showPhone, showVAT, showDescription, showTerms, logoBase64, footerLogosBase64,showPayment }) => {
  const calculateSubtotal = () => {
    return data?.items?.reduce((acc, item) => acc + parseFloat(item.price || 0), 0) || 0;
  };

  const calculateDiscountedTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = parseFloat(data?.discount) || 0;
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
  };

  const calculateTax = () => {
    const discountedTotal = calculateDiscountedTotal();
    const taxAmount = parseFloat(data?.tax) || 0;
    return (discountedTotal * taxAmount) / 100;
  };

  const calculateTotal = () => {
    return calculateDiscountedTotal() + calculateTax();
  };

  const convertDateFormat = (dateString) => {
    if (dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }
    return '';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header} fixed>
          <View style={styles.companyInfo}>
            {showLogo && logoBase64 && (
              <Image style={styles.logo} src={logoBase64} />
            )}
            <Text style={{ fontSize: 11, fontWeight: 'bold' }}>{data?.company?.name || 'R-Wire Limited'}</Text>
            {showAddress && <Text style={{ fontSize: 8, width: 125, lineHeight: 1.3, color: '#777474' }}>{data?.company?.address}</Text>}
            {showAddress && <Text style={{ fontSize: 8, width: 140, marginTop: 10, color: '#535151' }}>VAT reg no: {data?.company?.vat}</Text>}
            {showPhone && (
              <Text style={{ fontSize: 8, marginTop: 0, color: '#535151' }}>
                Email:
                <Text style={{ textDecoration: 'underline', fontSize: 8, marginTop: 0, color: '#535151' }}>
                  {data?.company?.email || ''}
                </Text>
              </Text>
            )}
          </View>
          <View>
            <Text style={{ fontSize: 8, color: '#535151' }}>Issue Date: {convertDateFormat(data?.issue_date)}</Text>
          </View>
        </View>
        <View style={styles.invoiceTitle}>
          <Text>QUOTE</Text>
        </View>
        <View style={styles.table}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View style={{ width: '50%' }}>
              <Text style={{ fontWeight: 'bold', fontSize: 11 }}>Quote To:</Text>
              <Text style={{ fontSize: 10, color: '#000' }}>{data?.customer_name || 'Akira'}</Text>
              <Text style={{ fontSize: 9, color: '#535151' }}>{data?.phone || '07805410487'}</Text>
              <Text style={{ fontSize: 9, width: 140, color: '#535151' }}>{data?.address || '9-13 summertimee av'}</Text>
            </View>
            <View style={{ width: '50%', textAlign: 'right' }}>
              <Text style={{ fontSize: 9, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Quote No</Text>: {data?.invoice_number || 'RW-058'}</Text>
              {data?.order_number && <Text style={{ fontSize: 9, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Order No</Text>: {data.order_number}</Text>}
            </View>
          </View>
        </View>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableColDesc, { fontWeight: 'bold', fontSize: 11 }]}>Description</Text>
            <Text style={[styles.tableColQty, { fontWeight: 'bold', fontSize: 11 }]}>Quantity</Text>
            <Text style={[styles.tableColUnitPrice, { fontWeight: 'bold', fontSize: 11 }]}>Unit Price</Text>
            <Text style={[styles.tableColPrice, { fontWeight: 'bold', fontSize: 11 }]}>Cost</Text>
          </View>
          {data?.items?.filter(item => item.item_name && item.price).map((item, index) => (
            <View
              key={index}
              style={{
                ...styles.tableRow,
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f3f3f3',
              }}
            >
              <View style={styles.tableColDesc}>
                <Text>{item.item_name || 'N/A'}</Text>
                {showDescription && (
                  <View style={{ marginTop: 0 }}>
                    {parseHtmlToPdf(item.description || 'N/A')}
                  </View>
                )}
              </View>
              <Text style={[styles.tableColQty,{ paddingTop:item.description ? 20 : 0}]}>{item.quantity || 1}</Text>
              <Text style={[styles.tableColUnitPrice,,{paddingTop:item.description ? 20 : 0}]}>£{parseFloat(item.unit_price || 0).toFixed(2)}</Text>
              <Text style={[styles.tableColPrice,,{paddingTop:item.description ? 20 : 0}]}>£{parseFloat(item.price || 0).toFixed(2)}</Text>
            </View>
          ))}
        </View>
        <View style={[styles.paymentSection]}>
          {/* <View style={styles.paymentInstructions}>
            <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Payment Instructions</Text>
            <Text style={{ fontSize: 9, marginBottom: -4 }}>{data?.company?.accountTitle || 'R-Wire Limited'}</Text>
            <Text style={{ fontSize: 9, marginBottom: -4 }}>Sort code: {data?.company?.sortCode || '09-01-29'}</Text>
            <Text style={{ fontSize: 9, marginBottom: -4 }}>Account no: {data?.company?.accountNumber || '35103808'}</Text>
          </View> */}
          <View style={[styles.totals,{width: showPayment? '50%': '100%'}]}>
            <Text style={[styles.totalRow, { fontSize: 10, fontWeight: 500 }]}>Total Net Amount: £{calculateSubtotal().toFixed(2)}</Text>
            {data?.discount > 0 && (
              <Text style={[styles.totalRow, { fontSize: 10, fontWeight: 500 }]}>
                Discount: £{((calculateSubtotal() * (parseFloat(data?.discount) || 0)) / 100).toFixed(2)}
              </Text>
            )}
            {data?.tax > 0 && (
              <Text style={[styles.totalRow, { fontSize: 10, fontWeight: 500 }]}>Total VAT Amount: £{calculateTax().toFixed(2)}</Text>
            )}
            <View style={styles.divider} />
            <Text style={[styles.totalRowBold, { fontSize: 11 }]}>Invoice Total: £{calculateTotal().toFixed(2)}</Text>
            <View style={styles.divider} />
          </View>
        </View>
        {showTerms && (
          <View style={styles.termsSection} break>
            <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Terms and Conditions:</Text>
            <Text style={{ marginTop: 8, fontSize: 9, color: '#535151' }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Payment Due Date:</Text> Full payment is due within 14 days from the invoice date.
            </Text>
            <Text style={{ fontSize: 9, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Late Payments:</Text> A late fee of 5% of the total invoice amount will be applied to any payment not received by the due date. Interest on overdue balances will accrue at 5% per week until payment is received in full.
            </Text>
            <Text style={{ fontSize: 9, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Methods of Payment:</Text> Payment can be made via bank transfer. Please ensure the invoice number is included in all payments for proper processing.
            </Text>
            <Text style={{ fontSize: 9, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Disputes:</Text> Any disputes regarding the invoice must be reported in writing within 7 days of the invoice date. Disputing any portion of the invoice does not suspend the obligation to pay the undisputed amount by the due date.
            </Text>
            <Text style={{ fontSize: 9, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Suspension of Service:</Text> Non-payment may result in suspension of service or further legal action, at the discretion of R-Wire Limited.
            </Text>
            <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>Disclaimer:</Text>
            <Text style={{ fontSize: 9, color: '#535151', lineHeight: 1.6 }}>
              If payment of invoices is not received, we reserve the right to recover any cost, commissions or fees incurred for the collection of payments including those which may be incurred by using a debt collection agency. There will be a 5% late payment fee applied every 7 days after the due date of the invoice.
            </Text>
            <Text style={{ fontWeight: 'bold', fontSize: 10, marginTop: 15 }}>
              {data?.company?.name || 'R-Wire Limited'}
            </Text>
            <Text style={{ fontSize: 9 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
            <Text style={{ fontSize: 9 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
          </View>
        )}
        <View fixed style={styles.footerLogos}>
          {footerLogosBase64.main && 
            <View style={{ width: '90%' }}>
              <Image style={styles.footerLogo} src={footerLogosBase64.main} />
            </View>
          }
        </View>
      </Page>
    </Document>
  );
};
// Main Invoice Preview Component
const ShowReportLayer = ({ data }) => {
  const [spinner, setSpinner] = useState(false);
  const [message, setMessage] = useState({
    status: false,
    reason: '',
    desc: '',
  });
  const [showModal, setShowModal] = useState(false);
  const [sendMethod, setSendMethod] = useState('email');
  const [sendValue, setSendValue] = useState('');
  

  return (
    <div className="container py-4">
       
       <div>
        <ReportPDF initialData={data}/>
       </div>

      {/* Modal for Send Invoice */}
    
    </div>
  );
};

export default ShowReportLayer;